import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageCircle, Check, Reply, Trash2, RotateCcw, Send } from 'lucide-react';
import clsx from 'clsx';
import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';

interface Comment {
  id: string;
  chapterId: string;
  parentCommentId: string | null;
  authorUserId: string;
  body: string;
  resolvedAt: number | null;
  resolvedBy: string | null;
  createdAt: number;
  updatedAt: number;
  authorName: string | null;
  authorEmail: string | null;
  authorPicture: string | null;
}

interface Props {
  chapterId: string;
}

export default function CommentsSection({ chapterId }: Props) {
  const { user, impersonator } = useAuth();
  const qc = useQueryClient();
  const readOnly = !!impersonator; // admin v impersonaci nemůže nic měnit
  const [showResolved, setShowResolved] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['comments', chapterId],
    queryFn: () => api.get<{ comments: Comment[] }>(`/chapters/${chapterId}/comments`),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['comments', chapterId] });

  const create = useMutation({
    mutationFn: (body: { body: string; parent_comment_id?: string | null }) =>
      api.post(`/chapters/${chapterId}/comments`, body),
    onSuccess: invalidate,
  });
  const resolve = useMutation({
    mutationFn: (id: string) => api.post(`/comments/${id}/resolve`),
    onSuccess: invalidate,
  });
  const unresolve = useMutation({
    mutationFn: (id: string) => api.post(`/comments/${id}/unresolve`),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/comments/${id}`),
    onSuccess: invalidate,
  });

  const { threads, openCount, resolvedCount } = useMemo(() => {
    const all = data?.comments ?? [];
    const top = all.filter((c) => !c.parentCommentId);
    const byParent = new Map<string, Comment[]>();
    for (const c of all) {
      if (c.parentCommentId) {
        const arr = byParent.get(c.parentCommentId) ?? [];
        arr.push(c);
        byParent.set(c.parentCommentId, arr);
      }
    }
    const threads = top.map((t) => ({ root: t, replies: byParent.get(t.id) ?? [] }));
    const openCount = top.filter((t) => !t.resolvedAt).length;
    const resolvedCount = top.length - openCount;
    return { threads, openCount, resolvedCount };
  }, [data]);

  const canResolve = user?.role === 'admin' || user?.role === 'editor';
  const visibleThreads = showResolved ? threads : threads.filter((t) => !t.root.resolvedAt);

  return (
    <div className="mt-10 border-t border-gray-200 pt-8">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-teal" />
          <h2 className="font-heading text-xl font-bold text-dark">
            Komentáře{openCount > 0 && <span className="ml-2 text-sm font-semibold text-teal">{openCount} nevyřešené</span>}
          </h2>
        </div>
        {resolvedCount > 0 && (
          <button
            onClick={() => setShowResolved((s) => !s)}
            className="text-xs text-gray-500 hover:text-teal"
          >
            {showResolved ? `Skrýt vyřešené (${resolvedCount})` : `Zobrazit vyřešené (${resolvedCount})`}
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-500">Načítám komentáře…</div>
      ) : threads.length === 0 ? (
        <div className="mb-6 rounded-md bg-gray-50 px-5 py-6 text-center text-sm text-gray-500">
          Zatím žádné komentáře. Napiš první dotaz nebo poznámku níže.
        </div>
      ) : (
        <div className="mb-6 space-y-5">
          {visibleThreads.map((t) => (
            <Thread
              key={t.root.id}
              root={t.root}
              replies={t.replies}
              meId={user?.id}
              canResolve={canResolve}
              readOnly={readOnly}
              onReply={(body) => create.mutate({ body, parent_comment_id: t.root.id })}
              onResolve={() => resolve.mutate(t.root.id)}
              onUnresolve={() => unresolve.mutate(t.root.id)}
              onDelete={(id) => {
                if (confirm('Smazat tento komentář?')) remove.mutate(id);
              }}
            />
          ))}
        </div>
      )}

      {readOnly ? (
        <div className="rounded-md border border-gold/30 bg-gold-soft/50 p-4 text-sm text-gray-600">
          Během impersonace nemůžeš psát komentáře.
        </div>
      ) : (
        <NewCommentForm
          onSubmit={(body) => create.mutate({ body })}
          isPending={create.isPending}
          placeholder="Napiš dotaz nebo poznámku…"
        />
      )}
    </div>
  );
}

function Thread({
  root,
  replies,
  meId,
  canResolve,
  readOnly,
  onReply,
  onResolve,
  onUnresolve,
  onDelete,
}: {
  root: Comment;
  replies: Comment[];
  meId?: string;
  canResolve: boolean;
  readOnly: boolean;
  onReply: (body: string) => void;
  onResolve: () => void;
  onUnresolve: () => void;
  onDelete: (id: string) => void;
}) {
  const [replyOpen, setReplyOpen] = useState(false);
  const resolved = !!root.resolvedAt;

  return (
    <div
      className={clsx(
        'rounded-lg border p-4 transition-colors',
        resolved ? 'border-gray-200 bg-gray-50' : 'border-gray-200 bg-white shadow-card',
      )}
    >
      <CommentBody c={root} meId={meId} readOnly={readOnly} onDelete={onDelete} dimmed={resolved} />

      {replies.length > 0 && (
        <div className="mt-3 space-y-3 border-l-2 border-gray-100 pl-4">
          {replies.map((r) => (
            <CommentBody key={r.id} c={r} meId={meId} readOnly={readOnly} onDelete={onDelete} dimmed={resolved} isReply />
          ))}
        </div>
      )}

      {!readOnly && (
        <div className="mt-3 flex items-center gap-2 border-t border-gray-100 pt-3">
          {!resolved && (
            <button
              onClick={() => setReplyOpen((o) => !o)}
              className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-gray-500 hover:bg-gray-100 hover:text-dark"
            >
              <Reply className="h-3 w-3" />
              Odpovědět
            </button>
          )}
          {canResolve &&
            (resolved ? (
              <button
                onClick={onUnresolve}
                className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-gray-500 hover:bg-gray-100 hover:text-dark"
              >
                <RotateCcw className="h-3 w-3" />
                Znovu otevřít
              </button>
            ) : (
              <button
                onClick={onResolve}
                className="flex items-center gap-1 rounded-md bg-positive-bg px-2 py-1 text-xs font-semibold text-positive hover:brightness-95"
              >
                <Check className="h-3 w-3" />
                Označit jako vyřešeno
              </button>
            ))}
          {resolved && root.resolvedAt && (
            <div className="ml-auto text-[11px] text-gray-400">
              Vyřešeno {new Date(root.resolvedAt * 1000).toLocaleString('cs-CZ')}
            </div>
          )}
        </div>
      )}

      {replyOpen && !readOnly && (
        <div className="mt-3">
          <NewCommentForm
            onSubmit={(body) => {
              onReply(body);
              setReplyOpen(false);
            }}
            placeholder="Napiš odpověď…"
            compact
            onCancel={() => setReplyOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

function CommentBody({
  c,
  meId,
  readOnly,
  onDelete,
  dimmed,
  isReply,
}: {
  c: Comment;
  meId?: string;
  readOnly: boolean;
  onDelete: (id: string) => void;
  dimmed?: boolean;
  isReply?: boolean;
}) {
  const own = c.authorUserId === meId;
  return (
    <div className={clsx('flex gap-3', dimmed && 'opacity-75')}>
      {c.authorPicture ? (
        <img src={c.authorPicture} alt="" className={clsx('shrink-0 rounded-full', isReply ? 'h-7 w-7' : 'h-9 w-9')} />
      ) : (
        <div
          className={clsx(
            'grid shrink-0 place-items-center rounded-full bg-gray-100 font-bold text-gray-500',
            isReply ? 'h-7 w-7 text-xs' : 'h-9 w-9 text-sm',
          )}
        >
          {(c.authorName ?? '?')[0]?.toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="mb-0.5 flex items-baseline gap-2">
          <span className="text-sm font-semibold text-dark">{c.authorName ?? 'Neznámý'}</span>
          <span className="text-xs text-gray-400">
            {new Date(c.createdAt * 1000).toLocaleString('cs-CZ', {
              day: 'numeric',
              month: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
          {c.updatedAt > c.createdAt + 2 && <span className="text-[10px] text-gray-400">(upraveno)</span>}
        </div>
        <div className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">{c.body}</div>
      </div>
      {own && !readOnly && (
        <button
          onClick={() => onDelete(c.id)}
          className="shrink-0 self-start rounded-md p-1 text-gray-300 hover:bg-danger/10 hover:text-danger"
          title="Smazat vlastní komentář"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function NewCommentForm({
  onSubmit,
  isPending,
  placeholder,
  compact,
  onCancel,
}: {
  onSubmit: (body: string) => void;
  isPending?: boolean;
  placeholder: string;
  compact?: boolean;
  onCancel?: () => void;
}) {
  const [body, setBody] = useState('');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const trimmed = body.trim();
        if (!trimmed) return;
        onSubmit(trimmed);
        setBody('');
      }}
    >
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={placeholder}
        rows={compact ? 2 : 3}
        className="input resize-y text-sm"
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            const trimmed = body.trim();
            if (trimmed) {
              onSubmit(trimmed);
              setBody('');
            }
          }
        }}
      />
      <div className="mt-2 flex items-center justify-between">
        <div className="text-[11px] text-gray-400">
          Tip: <kbd className="rounded bg-gray-100 px-1 py-0.5 font-mono">⌘+Enter</kbd> odeslat
        </div>
        <div className="flex gap-2">
          {onCancel && (
            <button type="button" onClick={onCancel} className="btn-ghost">
              Zrušit
            </button>
          )}
          <button type="submit" disabled={!body.trim() || isPending} className="btn-primary">
            <Send className="h-3.5 w-3.5" />
            {isPending ? 'Odesílám…' : 'Odeslat'}
          </button>
        </div>
      </div>
    </form>
  );
}
